import { useRef } from "react";
import {
  motion,
  useReducedMotion,
  useMotionValue,
  useTransform,
} from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import Waveform from "@/components/Waveform";
import { useAuth } from "@/lib/AuthContext";

const EASE = [0.22, 1, 0.36, 1];
const HERO_PHOTO =
  "https://images.unsplash.com/photo-1531651008558-ed1740375b39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxtb25vY2hyb21lJTIwbWljcm9waG9uZSUyMGFic3RyYWN0JTIwZHJhbWF0aWMlMjBsaWdodGluZ3xlbnwwfHx8YmxhY2tfYW5kX3doaXRlfDE3OTIzNTU5Mjd8MA&ixlib=rb-4.1.0&q=85";



function MaskedLine({ children, delay, reduce, className = "" }) {
  return (
    <span className="-mb-[0.06em] block overflow-hidden pb-[0.06em]">
      <motion.span
        className={`block ${className}`}
        initial={{ y: reduce ? 0 : "112%" }}
        animate={{ y: 0 }}
        transition={{ delay: reduce ? 0 : delay, duration: 1.05, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { user } = useAuth();
  const reduce = useReducedMotion();
  const sectionRef = useRef(null);

  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useTransform(my, [0, 1], [7, -7]);
  const rotateY = useTransform(mx, [0, 1], [-7, 7]);

  return (
    <section id="top" ref={sectionRef} data-testid="hero-section" className="relative overflow-hidden">
      

      <div className="mx-auto max-w-6xl px-5 pb-24 pt-10 sm:px-8 sm:pt-14">
        <motion.div
          initial={{ opacity: reduce ? 1 : 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: reduce ? 0 : 0.9, duration: 0.8 }}
          className="flex items-center justify-between border-b border-neutral-200 pb-4 text-[11px] font-bold uppercase tracking-[0.25em] text-neutral-500"
        >
          <span>Savy Kerno®</span>
          <span className="hidden sm:block">AI Voice + Marketing Platform</span>
          <span>EN · हिन्दी · తెలుగు</span>
        </motion.div>

        <div className="mt-10 grid items-center gap-12 sm:mt-14 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
          <div>
            <h1
              data-testid="hero-headline"
              className="text-[12vw] font-black leading-[0.96] tracking-[-0.035em] text-[#111111] sm:text-[9vw] lg:text-[4.3rem] xl:text-[5rem]"
            >
              <MaskedLine delay={1.1} reduce={reduce}>AI that talks.</MaskedLine>
              <MaskedLine delay={1.22} reduce={reduce} className="text-outline">Marketing</MaskedLine>
              <MaskedLine delay={1.34} reduce={reduce}>
                <span className="text-neutral-300">that grows.</span>
              </MaskedLine>
            </h1>

            <motion.div
              initial={{ opacity: reduce ? 1 : 0, y: reduce ? 0 : 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: reduce ? 0 : 1.55, duration: 0.9, ease: EASE }}
            >
              <p data-testid="hero-subtext" className="mt-7 max-w-md text-base leading-relaxed text-neutral-500 sm:text-lg">
                One intelligent platform for AI voice agents, customer relationships
                and digital marketing.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href={user ? "/dashboard" : "/signup"}
                  data-testid="hero-get-started-button"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#111111] px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-neutral-900/10 transition-all hover:bg-neutral-800 hover:shadow-xl"
                >
                  {user ? "Open Dashboard" : "Get Started"}
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                </a>
                <a
                  href="#product"
                  data-testid="hero-explore-demo-button"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-neutral-300 bg-white px-8 py-4 text-sm font-semibold text-[#111111] transition-all hover:border-neutral-400 hover:bg-neutral-50"
                >
                  <Play size={15} className="fill-current" />
                  Explore Demo
                </a>
              </div>
              <div className="mt-12 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.25em] text-neutral-400">
                <motion.span
                  className="block h-px w-10 origin-left bg-neutral-300"
                  animate={reduce ? {} : { scaleX: [1, 0.4, 1] }}
                  transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                />
                Scroll
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: reduce ? 1 : 0, y: reduce ? 0 : 48 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: reduce ? 0 : 1.7, duration: 1.1, ease: EASE }}
            className="relative pb-10"
            style={{ perspective: 1100 }}
            onMouseMove={(e) => {
              if (reduce) return;
              const r = e.currentTarget.getBoundingClientRect();
              mx.set((e.clientX - r.left) / r.width);
              my.set((e.clientY - r.top) / r.height);
            }}
            onMouseLeave={() => {
              mx.set(0.5);
              my.set(0.5);
            }}
          >
            <motion.div
              style={reduce ? {} : { rotateX, rotateY, transformStyle: "preserve-3d" }}
              className="relative"
            >
              <div className="overflow-hidden rounded-[2rem] border border-neutral-200 bg-neutral-100">
                <img
                  src={HERO_PHOTO}
                  alt=""
                  className="aspect-[4/3] w-full object-cover grayscale"
                  loading="eager"
                />
              </div>
              <div
                data-testid="hero-waveform-card"
                className="absolute -bottom-8 left-4 right-4 rounded-2xl border border-neutral-200 bg-white/90 p-5 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.28)] backdrop-blur sm:left-8 sm:right-auto sm:w-80"
                style={reduce ? {} : { transform: "translateZ(50px)" }}
              >
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-neutral-200" />
                    <span className="h-2 w-2 rounded-full bg-neutral-200" />
                    <span className="h-2 w-2 rounded-full bg-neutral-200" />
                  </div>
                  <span className="rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
                    Visual preview
                  </span>
                </div>
                <Waveform compact />
                <p className="mt-3 text-[11px] leading-relaxed text-neutral-400">
                  Browser voice demo, shown with sample audio — real phone answering switches on once a telephony provider is connected to your account.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
